import { ChevronDown } from "@faire/slate-icons/ChevronDown";
import { ChevronUp } from "@faire/slate-icons/ChevronUp";
import { Close } from "@faire/slate-icons/Close";
import { Button } from "@faire/slate/components/button";
import { Checkbox } from "@faire/slate/components/checkbox";
import { FilterToggle } from "@faire/slate/components/filter";
import { Link } from "@faire/slate/components/link";
import {
  BaseModal,
  BaseModalBody,
  BaseModalFooter,
  BaseModalHeader,
} from "@faire/slate/components/modal/base";
import { RadioGroup } from "@faire/slate/components/radio";
import { Search } from "@faire/slate/components/search";
import { tsv } from "@faire/slate/lib/tsv";
import React, { useState } from "react";

import { SlateStyleOverride } from "../../../../components/shared";
import type {
  CheckboxFilterSection,
  FilterSection,
  LinkFilterSection,
  RadioFilterSection,
  RangeFilterSection,
} from "../constants";
import { FILTER_SECTIONS } from "../constants";

import { APPLIED_CHIP_OUTLINE } from "./appliedChipOutline";
import { PillFilterSectionContent } from "./PillFilterSectionContent";
import { RangeControl } from "./RangeControl";
import type { RangeValue } from "./RangeSlider";
import { RecommendedFiltersSection } from "./RecommendedFiltersSection";

/**
 * An applied filter surfaced in the modal's "Selected" summary. Filters applied
 * from the chip row appear here too, so the summary matches the count badge
 * rather than only listing what was picked inside the modal.
 */
export interface AppliedFilter {
  key: string;
  label: string;
  onRemove: () => void;
}

interface AllFiltersModalProps {
  open: boolean;
  onClose: () => void;
  /** Slate width variant — "medium" is 500px, "large" is 648px. */
  size: "medium" | "large";
  /** Modal height as a percentage of viewport height. */
  heightVh: number;
  /** Drop shadows on the pinned header and footer while the body is scrolled. */
  scrollShadows: boolean;
  /** Product count shown on the primary action. */
  resultCount: number;
  /**
   * True while the result set is being recounted. The primary action shows a
   * spinner rather than a stale number.
   */
  isCounting?: boolean;
  /** Selected value per radio section, keyed by section key. */
  radioSelections: Record<string, string>;
  onRadioChange: (sectionKey: string, value: string) => void;
  /** Every checked checkbox value across sections. */
  checkedValues: Set<string>;
  onToggleChecked: (value: string) => void;
  onClearAll: () => void;
  /**
   * Every applied filter, from both the chip row and this modal. Optional so
   * other prototypes sharing this modal opt out of the summary by omitting it.
   */
  selectedFilters?: AppliedFilter[];
  /**
   * Current value for each range section, keyed by section key. Wholesale
   * price shares the Price chip's state; brand minimum is modal-only.
   */
  rangeValues?: Record<string, RangeValue>;
  onRangeChange?: (sectionKey: string, next: RangeValue) => void;
  /**
   * Distribution bars per range section, keyed by section key. Omitted by
   * consumers that don't compute them; the slider then renders bare.
   */
  rangeHistograms?: Record<string, number[]>;
  /**
   * Builds the centered read-out under each range slider, keyed by section
   * key. A formatter rather than a string so it can follow a drag in
   * progress.
   */
  rangeSummaries?: Record<string, (value: RangeValue) => string>;
  /** Applied one-tap filters, shared with the chip row. */
  appliedToggles?: Set<string>;
  onToggleFilter?: (key: string) => void;
}

/**
 * "All filters" modal, built to the Figma spec
 * (file EVLmXDKcBUfoDyzxqQj7yJ, node 2903:14604).
 *
 * Every section is collapsible and separated by a top rule. Selections live on
 * the page rather than here, so the results grid reflows behind the modal as
 * soon as a filter is picked — the primary action just dismisses.
 */
export const AllFiltersModal: React.FC<AllFiltersModalProps> = ({
  open,
  onClose,
  size,
  heightVh,
  scrollShadows,
  resultCount,
  isCounting = false,
  radioSelections,
  onRadioChange,
  checkedValues,
  onToggleChecked,
  onClearAll,
  selectedFilters = [],
  rangeValues = {},
  onRangeChange,
  rangeHistograms = {},
  rangeSummaries = {},
  appliedToggles,
  onToggleFilter,
}) => {
  // Sections all start open so the full filter set is scannable on open.
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(
    new Set()
  );
  const [expandedShowMore, setExpandedShowMore] = useState<Set<string>>(
    new Set()
  );
  /*
   * Whether the Selected summary appears is decided when the modal opens, not
   * as filters change. Applying the first filter otherwise makes the summary
   * appear and push every section down under the cursor. Reopen the modal and
   * the summary is there from the start.
   *
   * Adjusting state during render on a prop change is React's documented
   * alternative to an effect here — it settles before anything paints.
   */
  const [showSelectedSection, setShowSelectedSection] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);
  /*
   * Bumped on every open and handed to the location pickers, so each visit to
   * the modal starts from the collapsed "Add location" state rather than
   * however the last one was left. A token rather than a remount key: the
   * pickers keep the locations they have already moved into their pill rows.
   */
  const [openCount, setOpenCount] = useState(0);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setShowSelectedSection(selectedFilters.length > 0);
      setOpenCount((previous) => previous + 1);
    }
  }

  const isSelectedSectionVisible =
    showSelectedSection && selectedFilters.length > 0;

  // Other prototypes sharing this modal omit these props and get no section.
  const showRecommended =
    appliedToggles !== undefined && onToggleFilter !== undefined;

  // Covers the chip row too, since "Clear all" now clears everything.
  const hasSelections = selectedFilters.length > 0;

  const renderSectionContent = (section: FilterSection) => {
    switch (section.kind) {
      case "radio":
        return (
          <RadioSectionContent
            checked={radioSelections[section.key]}
            onChange={(value) => onRadioChange(section.key, value)}
            section={section}
          />
        );
      case "checkbox":
        return (
          <CheckboxSectionContent
            checkedValues={checkedValues}
            isShowingMore={expandedShowMore.has(section.key)}
            onToggleChecked={onToggleChecked}
            onToggleShowMore={() =>
              setExpandedShowMore((previous) =>
                toggleInSet(previous, section.key)
              )
            }
            section={section}
          />
        );
      case "pill":
        return (
          <PillFilterSectionContent
            checkedValues={checkedValues}
            onToggleChecked={onToggleChecked}
            resetToken={openCount}
            section={section}
          />
        );
      case "link":
        return (
          <LinkSectionContent
            isShowingMore={expandedShowMore.has(section.key)}
            onToggleShowMore={() =>
              setExpandedShowMore((previous) =>
                toggleInSet(previous, section.key)
              )
            }
            section={section}
          />
        );
      case "range": {
        // Only rendered when the page owns this range; other consumers of the
        // modal omit it rather than showing a dead control.
        const value = rangeValues[section.key];
        return value !== undefined && onRangeChange !== undefined ? (
          <RangeSectionContent
            appliedToggles={appliedToggles}
            histogramBuckets={rangeHistograms[section.key]}
            onChange={(next) => onRangeChange(section.key, next)}
            onToggleFilter={onToggleFilter}
            section={section}
            formatSummary={rangeSummaries[section.key]}
            value={value}
          />
        ) : null;
      }
    }
  };

  return (
    <BaseModal onClose={onClose} open={open} size={size}>
      <BaseModalHeader title="Filters" />
      <BaseModalBody>
        {open ? (
          <ModalStyleOverrides
            heightVh={heightVh}
            scrollShadows={scrollShadows}
          />
        ) : null}
        <div className="flex flex-col gap-(--slate-dimensions-6)">
          <SelectedFiltersSection
            filters={isSelectedSectionVisible ? selectedFilters : []}
          />
          {appliedToggles !== undefined && onToggleFilter !== undefined ? (
            <RecommendedFiltersSection
              appliedToggles={appliedToggles}
              hasContentAbove={isSelectedSectionVisible}
              onToggle={onToggleFilter}
            />
          ) : null}
          {FILTER_SECTIONS.map((section, sectionIndex) => (
            <FilterSectionShell
              key={section.key}
              collapsible={section.collapsible ?? true}
              hasContentAbove={
                sectionIndex > 0 || isSelectedSectionVisible || showRecommended
              }
              isExpanded={!collapsedSections.has(section.key)}
              onToggle={() =>
                setCollapsedSections((previous) =>
                  toggleInSet(previous, section.key)
                )
              }
              title={section.title}
            >
              {renderSectionContent(section)}
            </FilterSectionShell>
          ))}
        </div>
      </BaseModalBody>
      <BaseModalFooter variant="customSlot">
        {/* w-full so the two actions can sit at opposite ends; Slate's footer
            otherwise shrink-wraps this row. ml-auto keeps the primary action
            right-aligned even when "Clear all" isn't rendered. */}
        <div className="flex w-full items-center gap-(--slate-dimensions-3)">
          {hasSelections ? (
            <Button onClick={onClearAll} size="small" variant="plain">
              Clear all
            </Button>
          ) : null}
          <div className="ml-auto">
            <Button
              loading={isCounting}
              loadingLabel="Updating results"
              onClick={onClose}
              size="medium"
              variant="primary"
            >
              {`Show ${resultCount.toLocaleString()} products`}
            </Button>
          </div>
        </div>
      </BaseModalFooter>
    </BaseModal>
  );
};

/**
 * Style overrides Slate's own props can't express.
 *
 * **Pinned title.** Slate renders two copies of the title: an inline one that
 * scrolls with the body and a sticky one that fades in. Its fade-in threshold
 * works out to zero scroll for this modal, so both are fully opaque at once and
 * the title appears to jump as the inline copy slides past the pinned one.
 *
 * **Height.** Slate caps the modal at `80dvh` on desktop with no prop to change
 * it, so the height toggle overrides `max-height` directly. Content always
 * exceeds the cap here, so max-height sets the effective height.
 *
 * **Scroll shadows.** Slate already tracks both scroll edges as classes — the
 * sticky header gains `--visible` once scrolled off the top, and
 * `ModalFooterBase` adds `border-fs-border-muted` whenever the body is not at
 * the bottom. Keying the shadows off those means they reuse Slate's own scroll
 * detection instead of a second listener that could drift out of sync.
 *
 * `SlateStyleOverride` can't reach either — `BaseModal` portals to
 * `document.body`, so a `display: contents` wrapper's descendant selectors never
 * match. Hence a style element, mounted only while the modal is open. It targets
 * Slate's BEM classes and is disposable prototype debt that may break when Slate
 * updates. Unlayered rules beat Slate's `@layer components`, so no `!important`.
 */
const ModalStyleOverrides: React.FC<{
  heightVh: number;
  scrollShadows: boolean;
}> = ({ heightVh, scrollShadows }) => (
  <style>
    {/* Two classes, so this beats Slate's single-class --hidden rule. */}
    {`.fs-modal__header--sticky.fs-modal__header--hidden {
        opacity: 1;
        pointer-events: auto;
        /* At the top the first section's own rule draws the line under the
           title, so the header stays borderless until you scroll. Once scrolled
           (--visible) Slate's muted border takes over, matching the footer. */
        border-bottom-color: transparent;
        /* Slate leaves its shadow utility on the sticky header even while
           --hidden and relies on opacity 0 to conceal it — which the rule
           above defeats. Nothing is hidden above the header at the top of the
           body, so it stays flat until there is. */
        box-shadow: none;
      }
      /* Keep the inline copy's layout box so content offset is unchanged. */
      .fs-modal__header:not(.fs-modal__header--sticky) .fs-modal__title {
        visibility: hidden;
      }
      /* Slate moves initial focus to the sticky title to announce the modal.
         Slate normally hides that ring because the copy is invisible; now that
         it shows, drop it — the title is aria-hidden and tabIndex -1, so it is
         not keyboard reachable and no affordance is lost. */
      .fs-modal__header--sticky .fs-modal__title:focus,
      .fs-modal__header--sticky .fs-modal__title:focus-visible {
        outline: none;
      }
      .fs-modal {
        max-height: ${heightVh}dvh;
      }
      ${
        scrollShadows
          ? `/* --visible means the body has scrolled off the top. */
      .fs-modal__header--sticky.fs-modal__header--visible {
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
      }
      /* Slate adds this class only while the body is not at the bottom. */
      .fs-modal__footer.border-fs-border-muted {
        box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.05);
      }`
          : /* Off means off: Slate's own shadow utilities sit on the elements
               themselves, so suppressing only the rules above would leave a
               softer shadow behind and make the comparison meaningless. */
            `.fs-modal__header--sticky,
      .fs-modal__footer {
        box-shadow: none;
      }`
      }`}
  </style>
);

const toggleInSet = (source: Set<string>, value: string): Set<string> => {
  const next = new Set(source);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
};

/**
 * Collapsible section wrapper: a top rule, then a 14px medium title row with a
 * chevron that flips on expand, then the section's content.
 */
/**
 * Removable summary of everything currently applied, pinned above the sections
 * so a retailer can see and undo their choices without hunting for the section
 * each one came from.
 */
const SelectedFiltersSection: React.FC<{ filters: AppliedFilter[] }> = ({
  filters,
}) => {
  if (filters.length === 0) {
    return null;
  }

  return (
    <div className={selectedSectionStyle.cls()}>
      <span className="type-fs-paragraph-medium text-fs-text-primary">
        Selected
      </span>
      <div className="flex flex-wrap gap-(--slate-dimensions-2)">
        {filters.map((filter) => (
          <button
            key={filter.key}
            aria-label={`Remove ${filter.label}`}
            className={selectedPillStyle.cls()}
            onClick={filter.onRemove}
            type="button"
          >
            {filter.label}
            {/* The icon ships its own <title>, so hide the wrapper rather than
                let "Close" land in the pill's accessible name. */}
            <span aria-hidden className="flex">
              <Close height="12px" width="12px" />
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

const FilterSectionShell: React.FC<
  React.PropsWithChildren<{
    title: string;
    isExpanded: boolean;
    onToggle: () => void;
    /** Draws the separating rule. False for the first thing in the body. */
    hasContentAbove: boolean;
    /**
     * Sections Figma draws with a chevron. The three it draws without one are
     * always open and get a plain, unclickable heading.
     */
    collapsible: boolean;
  }>
> = ({
  title,
  isExpanded,
  onToggle,
  hasContentAbove,
  collapsible,
  children,
}) => {
  const label = (
    <span className="type-fs-paragraph-medium text-fs-text-primary flex-1 text-left">
      {title}
    </span>
  );

  return (
    <div className={sectionShellStyle.cls({ collapsible })}>
      {/* The rule and its top padding live on the header so the whole row —
          not just the label — toggles the section. */}
      {collapsible ? (
        <button
          aria-expanded={isExpanded}
          className={sectionHeaderStyle.cls({ hasContentAbove, collapsible })}
          onClick={onToggle}
          type="button"
        >
          {label}
          <SectionChevron isExpanded={isExpanded} />
        </button>
      ) : (
        <div
          className={sectionHeaderStyle.cls({ hasContentAbove, collapsible })}
        >
          {label}
        </div>
      )}
      {isExpanded || !collapsible ? children : null}
    </div>
  );
};

/*
 * No bottom rule: every `FilterSectionShell` already draws its own top rule, so
 * adding one here put two lines between this block and the first section.
 */
const selectedSectionStyle = tsv({
  base: [
    "flex flex-col gap-(--slate-dimensions-3)",
    "pt-(--slate-dimensions-4)",
  ],
});

/** Outlined pill matching the applied chips in the row behind the modal. */
const selectedPillStyle = tsv({
  base: [
    "flex items-center gap-(--slate-dimensions-2) rounded-full border",
    "px-(--slate-dimensions-4) py-(--slate-dimensions-2)",
    "border-fs-text-primary text-fs-text-primary type-fs-paragraph",
    "hover:bg-fs-surface-tertiary cursor-pointer",
  ],
});

/* Figma sits the always-open sections tighter under their heading than the
   collapsible ones. */
const sectionShellStyle = tsv({
  base: "flex flex-col",
  variants: {
    collapsible: {
      true: "gap-(--slate-dimensions-3)",
      false: "gap-(--slate-dimensions-2)",
    },
  },
});

const sectionHeaderStyle = tsv({
  base: [
    "flex w-full items-center",
    "gap-(--slate-dimensions-2)",
    "pt-(--slate-dimensions-4)",
  ],
  variants: {
    hasContentAbove: {
      true: "border-fs-border-muted border-t",
      false: null,
    },
    collapsible: {
      true: "cursor-pointer",
      false: null,
    },
  },
});

const SectionChevron: React.FC<{ isExpanded: boolean }> = ({ isExpanded }) => {
  const Chevron = isExpanded ? ChevronUp : ChevronDown;
  return <Chevron height="12px" width="12px" />;
};

/**
 * "Show more" affordance. The Figma spec renders this as a plain small Button in
 * Ships from and as underlined text in Category, so the variant is explicit.
 */
const ShowMoreToggle: React.FC<{
  isShowingMore: boolean;
  onToggle: () => void;
  as: "button" | "link";
}> = ({ isShowingMore, onToggle, as }) => {
  const label = isShowingMore ? "Show less" : "Show more";

  if (as === "button") {
    return (
      <div>
        <Button onClick={onToggle} size="small" variant="plain">
          {label}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <Link as="button" onClick={onToggle}>
        {label}
      </Link>
    </div>
  );
};

/**
 * A range section: the optional one-tap shortcut Figma places above the
 * control, then the histogram, slider, and centered read-out.
 */
const RangeSectionContent: React.FC<{
  section: RangeFilterSection;
  value: RangeValue;
  onChange: (next: RangeValue) => void;
  histogramBuckets: number[] | undefined;
  formatSummary: ((value: RangeValue) => string) | undefined;
  appliedToggles: Set<string> | undefined;
  onToggleFilter: ((key: string) => void) | undefined;
}> = ({
  section,
  value,
  onChange,
  histogramBuckets,
  formatSummary,
  appliedToggles,
  onToggleFilter,
}) => {
  const { quickToggle } = section;
  // The shortcut shares its key with a Recommended pill, so it only renders
  // for consumers that own that state.
  const showQuickToggle =
    quickToggle !== undefined &&
    appliedToggles !== undefined &&
    onToggleFilter !== undefined;

  return (
    <div className="flex flex-col gap-(--slate-dimensions-2)">
      {showQuickToggle ? (
        <SlateStyleOverride className={APPLIED_CHIP_OUTLINE}>
          <div className="flex">
            <FilterToggle
              applied={appliedToggles.has(quickToggle.key)}
              label={quickToggle.label}
              onClick={() => onToggleFilter(quickToggle.key)}
            />
          </div>
        </SlateStyleOverride>
      ) : null}
      <RangeControl
        histogramBuckets={histogramBuckets}
        histogramHeightPx={section.histogram?.heightPx}
        onChange={onChange}
        rangeMax={section.rangeMax}
        rangeMin={section.rangeMin}
        singleKnob={section.singleKnob}
        step={section.step}
        formatSummary={formatSummary}
        subject={section.title.toLowerCase()}
        value={value}
      />
    </div>
  );
};

const RadioSectionContent: React.FC<{
  section: RadioFilterSection;
  checked: string | undefined;
  onChange: (value: string) => void;
}> = ({ section, checked, onChange }) => (
  // RadioGroup spaces its options internally (12px), so tightening to 8px means
  // piercing into Slate. Reachable here because this subtree is our own JSX.
  <SlateStyleOverride className="[&_.fs-component-group>div]:gap-(--slate-dimensions-2)">
    <RadioGroup
      checked={checked}
      name={section.key}
      onChecked={(option) => onChange(option.value)}
      options={section.options}
    />
  </SlateStyleOverride>
);

const CheckboxSectionContent: React.FC<{
  section: CheckboxFilterSection;
  checkedValues: Set<string>;
  isShowingMore: boolean;
  onToggleChecked: (value: string) => void;
  onToggleShowMore: () => void;
}> = ({
  section,
  checkedValues,
  isShowingMore,
  onToggleChecked,
  onToggleShowMore,
}) => {
  const visibleOptions = isShowingMore
    ? [...section.options, ...(section.moreOptions ?? [])]
    : section.options;

  return (
    <div className="flex flex-col gap-(--slate-dimensions-3)">
      {section.searchPlaceholder ? (
        <Search
          aria-label={section.searchPlaceholder}
          placeholder={section.searchPlaceholder}
        />
      ) : null}

      {/* Own container so the options sit tighter than the 12px separating the
          search field and "Show more" from the group. */}
      <div className="flex flex-col gap-(--slate-dimensions-2)">
        {visibleOptions.map((option) => (
          <div
            key={option.value}
            className="flex items-center gap-(--slate-dimensions-3)"
          >
            <div className="flex-1">
              {/*
                Slate derives the input id from the label when none is given,
                so two sections offering "United States" would share an id and
                the second label would toggle the first section's checkbox.
                Option values are unique, so they make the id unique too.
              */}
              <Checkbox
                checked={checkedValues.has(option.value)}
                id={option.value}
                label={option.label}
                onChange={() => onToggleChecked(option.value)}
              />
            </div>
            {option.expandable ? (
              <ChevronDown height="12px" width="12px" />
            ) : null}
          </div>
        ))}
      </div>

      {section.moreOptions ? (
        <ShowMoreToggle
          as="button"
          isShowingMore={isShowingMore}
          onToggle={onToggleShowMore}
        />
      ) : null}
    </div>
  );
};

const LinkSectionContent: React.FC<{
  section: LinkFilterSection;
  isShowingMore: boolean;
  onToggleShowMore: () => void;
}> = ({ section, isShowingMore, onToggleShowMore }) => {
  const visibleOptions = isShowingMore
    ? [...section.options, ...(section.moreOptions ?? [])]
    : section.options;

  return (
    <div className="flex flex-col gap-(--slate-dimensions-2)">
      <div className="flex flex-col items-start gap-(--slate-dimensions-1)">
        {visibleOptions.map((option) =>
          option.value === section.activeValue ? (
            // Link is the one Slate component that accepts className; the design
            // sets the active category to medium weight.
            <Link
              key={option.value}
              as="button"
              className="type-fs-paragraph-medium"
            >
              {option.label}
            </Link>
          ) : (
            <button
              key={option.value}
              className="type-fs-paragraph text-fs-text-primary"
              type="button"
            >
              {option.label}
            </button>
          )
        )}
      </div>
      {section.moreOptions ? (
        <ShowMoreToggle
          as="link"
          isShowingMore={isShowingMore}
          onToggle={onToggleShowMore}
        />
      ) : null}
    </div>
  );
};
